import { TrackProductService } from '@analytics/events';
import type { Commerce, TrackedProductComponent } from '@analytics/models';
import { FALLBACK_LIST_NAME } from '@analytics/utils';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, inject, input } from '@angular/core';
import { componentViewModelSelector } from '@core/experience-editor-utils';
import { Store } from '@ngrx/store';
import type {
  RichTextField,
  TextField,
} from '@sitecore-jss/sitecore-jss-angular';
import includes from 'lodash/includes';
import { Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { selectBoilerProductAction } from '@innogy/feh/boilers';
import cloneDeep from 'lodash/cloneDeep';
import type { StoreProductData } from '@legacy/common-ui-models';
import { Router } from '@angular/router';
import type { MultiPriceStoreProductData } from '@innogy/feh/models';
import {
  computedContentSectionId,
  computedContentSectionTheme,
} from '@core/content-section';

import {
  StoreProductTrackingConfig,
  confirmStoreProductSelectionAction,
  selectStoreProductAction,
  selectStoreProductOverviewViewModel,
  toggleStoreProductSelectionAction,
} from '../../store';
import { mockStoreProductOverviewViewModel } from '../../mocks/mock-store-product-overview-vm';
import { StoreProductOverviewRendering } from '../../models/store-product-overview-rendering.interface';
import { getProductSuffixLabel } from '../../utils/get-product-suffix-label';
import { initializeStoreFunnelComponent } from '../../utils/initialize-store-funnel-component';

@Component({
  selector: 'wl-store-product-overview[rendering]',
  templateUrl: './store-product-overview.component.html',
  host: { ngSkipHydration: 'true' },
})
export class StoreProductOverviewComponent
  implements
    OnInit,
    OnDestroy,
    TrackedProductComponent<MultiPriceStoreProductData, Commerce>
{
  public readonly rendering = input.required<StoreProductOverviewRendering>();
  readonly context = input();
  readonly id = computedContentSectionId(this.rendering);
  readonly theme = computedContentSectionTheme(this.rendering);

  readonly viewModel$ = this.store$.select(
    componentViewModelSelector(
      selectStoreProductOverviewViewModel,
      mockStoreProductOverviewViewModel
    )
  );

  private readonly router = inject(Router);
  private readonly onDestroy$ = new Subject();

  constructor(
    protected readonly store$: Store,
    private readonly trackingConfig: StoreProductTrackingConfig,
    private readonly trackProductService: TrackProductService
  ) {}

  ngOnInit() {
    const productList = this.rendering().fields.ProductList;
    this.store$.dispatch(
      initializeStoreFunnelComponent(
        'productOverview',
        this.rendering().fields.FunnelSettings,
        productList?.length ? productList : undefined
      )
    );
  }

  get trackProductConfig() {
    return this.trackingConfig.getConfig(
      this.productType,
      this.productCategory
    );
  }

  get productType() {
    return this.rendering().fields.FunnelSettings?.fields?.ProductType?.value;
  }

  get productCategory() {
    return this.rendering().fields.FunnelSettings?.fields?.ProductCategory
      ?.value;
  }

  suffixLabel(product: MultiPriceStoreProductData) {
    return getProductSuffixLabel(
      product,
      this.rendering().fields.ProductPriceSuffixLabel
    );
  }

  stronglyTypedProduct(product: any) {
    const typedProduct = cloneDeep(product) as MultiPriceStoreProductData;
    typedProduct.content.secondaryButtonStyle += ' mb-2';
    return typedProduct;
  }

  productSelected(
    selectedProducts: MultiPriceStoreProductData[],
    product: MultiPriceStoreProductData
  ) {
    return includes(selectedProducts, product);
  }

  onSelect(product: MultiPriceStoreProductData) {
    if (this.allowMultipleProductsSelection) {
      this.store$.dispatch(toggleStoreProductSelectionAction({ product }));
    } else {
      this.store$.dispatch(selectStoreProductAction({ product }));
      this.store$.dispatch(selectBoilerProductAction({ product }));
      this.trackProductSelection();
    }
  }

  onSubmit(event: Event) {
    event.preventDefault();
    this.store$.dispatch(confirmStoreProductSelectionAction());
    this.trackProductSelection();
  }

  private trackProductSelection() {
    this.viewModel$
      .pipe(
        tap(({ selectedProducts }) => {
          const listName =
            this.rendering().fields.componentName.value || FALLBACK_LIST_NAME;

          selectedProducts.forEach((product, index) => {
            const commerce = this.trackProductConfig.mapToCommerce(product, {
              index,
              listName,
              numberOfProducts: 1,
            });
            this.trackProductService.trackSelection(commerce);
          });
        }),
        take(1),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  get allowMultipleProductsSelection(): boolean {
    return (
      this.rendering().fields.FunnelSettings?.fields
        ?.AllowMultipleProductsSelection?.value ?? false
    );
  }

  get nextPageButtonTextField(): TextField {
    return this.rendering().fields.NextPageButtonText;
  }

  get toggleProductTitle(): TextField {
    return this.rendering().fields.ToggleProductTitle;
  }

  get toggleProductDescription(): RichTextField {
    return this.rendering().fields.ToggleProductDescription;
  }

  get productListName() {
    return this.rendering().fields.componentName.value || FALLBACK_LIST_NAME;
  }

  showPrices(): boolean {
    return !this.rendering().fields.FunnelSettings?.fields?.HidePrices?.value;
  }

  handleSecondaryButtonClick(data: StoreProductData) {
    this.router.navigate([data.content.secondaryButtonLink?.href]);
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }
}
